import styled from 'styled-components';
import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../store/hooks';
import { logout } from '../../store/slices/authSlice';

interface HeaderProps {
  $isExpanded: boolean;
  $hideOnMobile?: boolean;
}

const StyledHeaderContainer = styled.header<HeaderProps>`
  height: ${({ theme }) => theme.sizes.header.height};
  background-color:#1C0838;
  position: fixed;
  top: 0;
  right: 0;
  left: ${({ theme, $isExpanded }) => 
    $isExpanded ? theme.sizes.sidebar.expanded : theme.sizes.sidebar.collapsed};
  z-index: ${({ theme }) => theme.zIndex.header};
  display: flex;
  align-items: center;
  padding: 0 ${({ theme }) => theme.spacing.lg};
  transition: left 0.3s ease;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: ${({ $hideOnMobile }) => $hideOnMobile ? 'none' : 'none'}; /* Always hide on mobile for now */
  }
`;

const Navigation = styled.nav`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-left: ${({ theme }) => theme.spacing.xl};
`;

const SectionTitle = styled.span`
  color: #fff;
  font-weight: 600;
  font-size: 15px;
  letter-spacing: 0.2px;
`;

const NavItem = styled(Link)<{ $active?: boolean }>`
  color: ${({ theme, $active }) =>
    $active ? '#fff' : 'rgba(255, 255, 255, 0.6)'};
  text-decoration: none;
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.sm}`};
  position: relative;
  font-size: 15px;
  letter-spacing: 0.2px;

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 50%;
    transform: translateX(-50%);
    width: 16px;
    height: 2px;
    background-color: ${({ $active }) =>
      $active ? '#fff' : 'transparent'};
    transition: background-color 0.2s ease;
  }

  &:hover {
    color: #fff;
  }
`;

const Divider = styled.span`
  color: rgba(255, 255, 255, 0.4);
  margin: 0 ${({ theme }) => theme.spacing.md};
  font-size: 40px;
  font-weight: 300;
  transform: scaleX(0.3);
  display: inline-block;
  opacity: 0.8;
  height: 24px;
  line-height: 24px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  position: relative;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  background-color: #FF3B3B;
  border-radius: 50%;
  border: 2px solid #1C0838;
`;

const ProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
`;

const ProfileImage = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
`;

const ProfileName = styled.span`
  color: #fff;
  font-size: 14px;
  font-weight: 500;
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background-color: #1C1628;
  border-radius: 8px;
  min-width: 320px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
`;

const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  h2 {
    color: #fff;
    font-size: 16px;
    font-weight: 500;
  }
`;

const ViewAll = styled.span`
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 4px;
  margin-left: 12px;

  &:hover {
    color: #fff;
  }
`;

const ProfileCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  margin: 8px;
  border-radius: 8px;
  border: 1px solid rgba(147, 51, 234, 0.5);
  background: rgba(147, 51, 234, 0.1);

  img {
    width: 40px;
    height: 40px;
    border-radius: 8px;
  }

  div {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
`;

const RelationshipCount = styled.span`
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
`;

const RelationshipsSection = styled.div`
  padding: 16px;

  h3 {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 12px;

    span {
      display: flex;
      align-items: center;
      gap: 6px;

      img {
        width: 16px;
        height: 16px;
        opacity: 0.6;
      }
    }
  }
`;

const ExploreLink = styled.a`
  font-size: 12px;
  color: #9333EA;
  display: flex;
  align-items: center;
  gap: 4px;
  text-decoration: none;
  
  img {
    width: 14px;
    height: 14px;
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  background: transparent;
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #ff5252;
  font-size: 14px;
  font-weight: 500;
  padding: 16px 20px;
  width: 100%;
  text-align: left;
  cursor: pointer;
  margin-top: 8px;
  
  &:hover {
    background-color: rgba(255, 0, 0, 0.05);
  }
`;

const LogoutIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RelationshipItem = styled.a`
  display: flex;
  align-items: center;
  padding: 8px;
  gap: 12px;
  text-decoration: none;
  border-radius: 4px;
  transition: background-color 0.2s;

  img.enterprise-icon {
    width: 32px;
    height: 32px;
    border-radius: 4px;
  }

  div {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  h4 {
    color: #fff;
    font-size: 14px;
    font-weight: 500;
  }

  span {
    color: rgba(255, 255, 255, 0.6);
    font-size: 13px;
  }

  img.chevron {
    width: 16px;
    height: 16px;
    opacity: 0.6;
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const RightSection = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding-left: ${({ theme }) => theme.spacing.xl};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 1px;
    height: 24px;
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const Header: React.FC<{ isExpanded: boolean, hideOnMobile?: boolean }> = ({ isExpanded, hideOnMobile = false }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Determine if we're on a Home-related page
  const isHomePage = ["/", "/enterprises", "/trending", "/insights"].includes(location.pathname);

  // Determine if we're on a Profile-related page
  const isContentPage = location.pathname === "/content";
  
  // Determine if we're on a Wallet-related page
  const isWalletPage = location.pathname === "/wallet" || location.pathname.startsWith("/wallet/");
  
  // Determine if we're on a Profile-related page
  const isProfilePage = location.pathname === "/profile";
  
  return (
    <StyledHeaderContainer $isExpanded={isExpanded} $hideOnMobile={hideOnMobile}>
      {isHomePage && (
        <Navigation>
          <SectionTitle>Home</SectionTitle>
          <Divider>|</Divider>
          <NavItem to="/enterprises" $active={location.pathname === '/enterprises'}>Enterprise</NavItem>
          <NavItem to="/trending" $active={location.pathname === '/trending'}>Trending</NavItem>
          <NavItem to="/insights" $active={location.pathname === '/insights'}>Insights</NavItem>
        </Navigation>
      )}
      
      {isContentPage && (
        <Navigation>
          <SectionTitle>Content</SectionTitle>
        </Navigation>
      )}
      
      {isWalletPage && (
        <Navigation>
          <SectionTitle>Wallet</SectionTitle>
          <Divider>|</Divider>
          <NavItem to="/wallet/earnings" $active={location.pathname === '/wallet/earnings'}>Earnings</NavItem>
          <NavItem to="/wallet/referrals" $active={location.pathname === '/wallet/referrals'}>Referrals</NavItem>
          <NavItem to="/wallet/payouts" $active={location.pathname === '/wallet/payouts'}>Payouts</NavItem>
        </Navigation>
      )}
      
      {isProfilePage && (
        <Navigation>
          <SectionTitle>Profile</SectionTitle>
        </Navigation>
      )}
      <RightSection>
        <IconButton>
          <img src="/nav-icons/notification.svg" alt="Notifications" />
          <NotificationBadge />
        </IconButton>
        <ProfileSection onClick={() => setIsDropdownOpen(!isDropdownOpen)} ref={dropdownRef}>
          <ProfileImage src="/nav-icons/profile-icon.svg" alt="Profile" />
          <ProfileName>Chris Burkard</ProfileName>
          <img 
            src="/nav-icons/down.svg" 
            alt="Dropdown" 
            width="16" 
            height="16" 
            style={{ 
              transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0)', 
              transition: 'transform 0.2s ease' 
            }} 
          />
          <DropdownMenu $isOpen={isDropdownOpen}>
            <DropdownHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h2>Accounts</h2>
                <ViewAll>View All (2)</ViewAll>
              </div>
              <CloseButton onClick={() => setIsDropdownOpen(false)}>✕</CloseButton>
            </DropdownHeader>
            
            <ProfileCard>
              <img src="/nav-icons/profile-icon.svg" alt="Chris Burkard" />
              <div>
                <ProfileName>Chris Burkard</ProfileName>
                <RelationshipCount>3 Relations</RelationshipCount>
              </div>
            </ProfileCard>

            <RelationshipsSection>
              <h3>
                <span>
                  Relationships
                  <img src="/nav-icons/info-circle.svg" alt="" />
                </span>
                <ExploreLink href="#">
                  <img src="/nav-icons/proicons_compass.svg" alt="" />
                  Explore Enterprises
                </ExploreLink>
              </h3>

              <RelationshipItem href="#">
                <img className="enterprise-icon" src="/images/kayak-icon.svg" alt="Kayak" />
                <div>
                  <h4>Kayak</h4>
                  <span>Kayak - Enterprise Creator</span>
                </div>
                <img className="chevron" src="/nav-icons/down.svg" style={{ transform: 'rotate(-90deg)' }} alt="" />
              </RelationshipItem>

              <RelationshipItem href="#">
                <img className="enterprise-icon" src="/images/emirates-icon.svg" alt="Emirates" />
                <div>
                  <h4>Emirates</h4>
                  <span>Marketer</span>
                </div>
                <img className="chevron" src="/nav-icons/down.svg" style={{ transform: 'rotate(-90deg)' }} alt="" />
              </RelationshipItem>
            </RelationshipsSection>

            <LogoutButton onClick={() => {
              dispatch(logout());
              navigate('/login');
            }}>
              <LogoutIcon>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.90002 7.55999C9.21002 3.95999 11.06 2.48999 15.11 2.48999H15.24C19.71 2.48999 21.5 4.27999 21.5 8.74999V15.27C21.5 19.74 19.71 21.53 15.24 21.53H15.11C11.09 21.53 9.24002 20.08 8.91002 16.54" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M15 12H3.62" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5.85 8.65002L2.5 12L5.85 15.35" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </LogoutIcon>
              <span>Logout</span>
            </LogoutButton>
          </DropdownMenu>
        </ProfileSection>
      </RightSection>
    </StyledHeaderContainer>
  );
};

export default Header;
